import { useEffect, useState } from "react";
import { api } from "../api.js";

export interface Announcement {
  id: string;
  type: "update" | "maintenance" | "incident" | "news";
  title: string;
  body: string;
  linkUrl: string | null;
  isImportant: boolean;
  notifyBell: boolean;
  publishedAt: string | null;
  createdAt: string;
  isRead: boolean;
}

const TYPE_LABEL: Record<string, string> = {
  update: "アップデート",
  maintenance: "メンテナンス",
  incident: "障害",
  news: "お知らせ",
};

const TYPE_STYLE: Record<string, { bg: string; fg: string }> = {
  update: { bg: "#EEEDFE", fg: "#534AB7" },
  maintenance: { bg: "#FFF3E0", fg: "#B86E00" },
  incident: { bg: "#FCEBEB", fg: "#C62828" },
  news: { bg: "#E8F5E9", fg: "#1D9E75" },
};

function formatDate(s: string | null | undefined): string {
  if (!s) return "";
  const d = new Date(s);
  return `${d.getFullYear()}/${String(d.getMonth() + 1).padStart(2, "0")}/${String(d.getDate()).padStart(2, "0")}`;
}

/** リンク URL サニタイズ（javascript: 等を弾く） */
function safeUrl(url: string | null): string | null {
  if (!url) return null;
  if (!/^https?:\/\//i.test(url)) return null;
  return url;
}

/**
 * ダッシュボード上部に表示するお知らせバナー。
 * - 重要は赤枠で個別に上部固定
 * - 通常は「📣 お知らせ」セクション内にまとめて表示
 * - バックエンドで max 5 件に制限済み
 */
export function AnnouncementBanner() {
  const [items, setItems] = useState<Announcement[] | null>(null);

  useEffect(() => {
    api
      .getAnnouncements()
      .then((r) => setItems(r.announcements || []))
      .catch(() => setItems([]));
  }, []);

  if (!items || items.length === 0) return null;

  const important = items.filter((a) => a.isImportant);
  const regular = items.filter((a) => !a.isImportant);

  return (
    <div style={{ marginBottom: 24 }}>
      {/* 重要: 個別に赤枠表示 */}
      {important.map((a) => {
        const url = safeUrl(a.linkUrl);
        return (
          <div
            key={a.id}
            style={{
              border: "2px solid #d32f2f",
              background: "#FCEBEB",
              borderRadius: 8,
              padding: "14px 18px",
              marginBottom: 12,
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
              <span style={{ fontSize: 20 }}>⚠️</span>
              <span
                style={{
                  background: "#C62828",
                  color: "white",
                  padding: "3px 10px",
                  borderRadius: 12,
                  fontSize: 12,
                  fontWeight: 600,
                }}
              >
                {TYPE_LABEL[a.type]}
              </span>
              <span style={{ fontWeight: 700, fontSize: 15, color: "#1a1a1a" }}>{a.title}</span>
            </div>
            <div style={{ fontSize: 13, color: "#5a2828", whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
              {a.body}
            </div>
            {url && (
              <div style={{ marginTop: 8 }}>
                <a
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ fontSize: 13, color: "#C62828", textDecoration: "underline" }}
                >
                  詳細を見る
                </a>
              </div>
            )}
          </div>
        );
      })}

      {/* 通常: まとめて表示 */}
      {regular.length > 0 && (
        <div
          style={{
            background: "white",
            border: "1px solid #e8e7e3",
            borderRadius: 12,
            padding: "18px 20px",
          }}
        >
          <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 14, display: "flex", alignItems: "center", gap: 6 }}>
            <span>📣</span> お知らせ
          </div>
          {regular.map((a, i) => {
            const c = TYPE_STYLE[a.type];
            const url = safeUrl(a.linkUrl);
            return (
              <div
                key={a.id}
                style={{
                  padding: i === 0 ? "0 0 14px" : "14px 0",
                  borderTop: i === 0 ? "none" : "1px solid #f0efeb",
                  position: "relative",
                  paddingLeft: 20,
                }}
              >
                <span
                  style={{
                    position: "absolute",
                    left: 4,
                    top: i === 0 ? 6 : 20,
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    background: "#1a1a1a",
                  }}
                />
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4, flexWrap: "wrap" }}>
                  <span style={{ fontSize: 13, color: "#6b6b68" }}>{formatDate(a.publishedAt || a.createdAt)}</span>
                  <span
                    style={{
                      background: c.bg,
                      color: c.fg,
                      padding: "2px 10px",
                      borderRadius: 10,
                      fontSize: 12,
                      fontWeight: 600,
                    }}
                  >
                    {TYPE_LABEL[a.type]}
                  </span>
                  <span style={{ fontSize: 14, fontWeight: 600 }}>{a.title}</span>
                </div>
                <div style={{ fontSize: 13, color: "#6b6b68", whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                  {a.body}
                </div>
                {url && (
                  <div style={{ marginTop: 6 }}>
                    <a
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ fontSize: 13, color: "#534AB7", textDecoration: "underline" }}
                    >
                      詳細を見る
                    </a>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
